import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress
} from '@mui/material';
import {
  Work as WorkIcon,
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { Appointment } from '../../services/appointmentService';
import { AppointmentType } from '../../services/appointmentTypeService';

interface AppointmentsListProps {
  appointments: Appointment[];
  appointmentTypes: AppointmentType[];
  loadingAppointments: boolean;
  theme: any;
  onAppointmentClick: (appointment: Appointment) => void;
}

const AppointmentsList: React.FC<AppointmentsListProps> = ({
  appointments,
  appointmentTypes,
  loadingAppointments,
  theme,
  onAppointmentClick
}) => {
  // Get appointment type name by ID
  const getAppointmentTypeName = (appointmentTypeId: string): string => {
    const appointmentType = appointmentTypes.find(type => type.id === appointmentTypeId);
    return appointmentType ? appointmentType.name : 'Unknown Type';
  };

  return (
    <Card sx={{ backgroundColor: theme.accentColor }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>Appointments</Typography>

        {loadingAppointments ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress />
          </Box>
        ) : appointments.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            No appointments found for this job.
          </Typography>
        ) : (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {appointments.map((appointment) => (
              <Grid item xs={12} key={appointment.id}>
                <Card
                  variant="outlined"
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                  onClick={() => onAppointmentClick(appointment)}
                >
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Box flex={1}>
                        <Typography variant="h6" gutterBottom>
                          {appointment.title}
                        </Typography>

                        <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
                          <WorkIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {getAppointmentTypeName(appointment.appointmentTypeId)}
                          </Typography>
                        </Box>

                        {appointment.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {appointment.description}
                          </Typography>
                        )}

                        <Box display="flex" alignItems="center" sx={{ mb: 0.5 }}>
                          <CalendarIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {new Date(appointment.startTime).toLocaleString('en-GB')} - {new Date(appointment.endTime).toLocaleString('en-GB')}
                          </Typography>
                        </Box>

                        {appointment.address && (
                          <Box display="flex" alignItems="center" sx={{ mb: 0.5 }}>
                            <LocationIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2">
                              {appointment.address}
                            </Typography>
                          </Box>
                        )}

                        {appointment.customerName && (
                          <Box display="flex" alignItems="center" sx={{ mb: 0.5 }}>
                            <PersonIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2">
                              {appointment.customerName}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

export default AppointmentsList;
